import { FeatureFlags } from 'src/types/feature_flags'

export type LpuCardsPageMetro = {
  metro?: {
    name?: string
    color?: string | null
  }
  walk_distance?: number | null
  walk_time?: number | null
}

export type LpuCardsPageLoyaltyProgram = {
  levels: Array<{
    need_spend: number
    percent_amount: number
  }>
  max_payment_percent: number
  welcome_bonus_amount?: number
}

export type LpuCardsPageBidPlacement = {
  bid: number
  place: number
  payment?: {
    summa?: number | null
  } | null
  get_detail_admin_url?: string
}

export type LpuCardsPageCheapestSynonym = {
  synonym_name: string
  price: number
}

export type LpuCardsPageServicePrice = {
  dtype?: {
    name?: string
  }
  price: number
}

export type LpuCardsPageSynonymPriceData = {
  synonym?: {
    id?: number | null
  } | null
  name?: string
  duration?: number | null
  price?: number | null
  price_with_club?: number | null
  synonym_dtype_id?: number | null
  has_intervals?: boolean
  allow_treatment_referral?: boolean
  requires_primary_consultation?: boolean
  has_primary_consultation_doctors?: boolean
  doctor_specialities?: Array<{
    name: string
    translit: string
  }>
}

export const LPU_CARD_TYPE = {
  LPU: 'lpu',
  PARTNER: 'partner',
} as const

export type LpuCardType = typeof LPU_CARD_TYPE[keyof typeof LPU_CARD_TYPE]

export type PartnerLpuSetData = {
  name: string
  rates_count: number
  stars: number
  translit: string
  all_lpus_has_license?: boolean
}

export type PartnerCardContextItem = {
  analyses_count: number
  discount: number
  laboratories_count: number
  logo_image?: {
    url?: string | null
  } | null
  partner_lpuset_data: PartnerLpuSetData
}

export type LpuCardsPageItem = {
  id: number
  name: string
  translit: string
  closed?: boolean
  stars?: number | null
  rates?: number | null
  drrates?: number | null
  cropped_logo?: string | null
  img_50?: string | { url: string | null }
  img_100?: string | { url: string | null }
  img_200?: string | { url: string | null }
  logo?: {
    priority_image_files?: Array<{
      file: {
        url: string
      }
      file_format: string
    }>
  }
  address?: string
  information?: string | null
  schedule_str?: string
  lputype?: {
    name?: string
  }
  lpu_loyalty_program?: LpuCardsPageLoyaltyProgram | null
  nearest_metros?: LpuCardsPageMetro[] | null
  lpu_addresses?: Array<{
    id?: number
    schedule_status?: string
    schedule_str?: string
    lat?: number
    lon?: number
    address?: string
    is_main?: boolean
  }>
  appointment_express?: boolean
  appointment_service_express?: boolean
  appointment_service_direct?: boolean
  lpu_phone?: string | null
  owner?: {
    soft_version?: string
  } | null
  prices_count?: number
  number_of_prices?: number
  analyses_count?: number
  positive_rate?: string
  license?: string | null
  bid?: unknown
  bid_placement?: LpuCardsPageBidPlacement[] | LpuCardsPageBidPlacement | null
  is_active_club_discount_on_diagnostics?: boolean
  is_exists_video_card?: boolean
  is_award_or_grand_prix_winner?: boolean
  minutes_until_close?: number | null
  service_available?: boolean
  nearest_appointment_express_datetime?: string | null
  has_appointment_service_express?: boolean
  has_appointment_service_direct?: boolean
  staff_appointment_type?: string | null
  cheapest_synonyms?: LpuCardsPageCheapestSynonym[] | null
  service_prices?: LpuCardsPageServicePrice[] | null
  synonym_price_data?: LpuCardsPageSynonymPriceData[] | null
  logo_image?: {
    url?: string | null
  } | null
  discount?: number | null
  laboratories_count?: number | null
  partner_lpuset_data?: PartnerLpuSetData | null
  card_type?: LpuCardType
  town: {
    translit: string
    name?: string
    region?: {
      name?: string
      country?: {
        name?: string
        is_russia?: boolean
      }
    }
  }
}

export type LpuCardsPage = {
  __items__: LpuCardsPageItem[]
  number?: number
  has_other_pages?: boolean
  has_previous?: boolean
  has_next?: boolean
  previous_page_number?: number | null
  next_page_number?: number | null
  page_range?: Array<number | null>
  paginator?: {
    num_pages?: number
    page_range?: Array<number | null>
  }
}

export type AnalysesCategoryContextItem = {
  name: string
  category_logo_image?: {
    url?: string | null
  } | null
  service_category: {
    translit: string
  }
}

export type PopularAnalysisContextItem = {
  analysis_synonym_id: number
  biomaterial_id: number
  biomaterial_name: string
  execution_days: number
  name: string
  price: number
  price_with_discount: number
}

export type PopularTypeContextItem = {
  service_type_id: number
  service_type_name: string
  page_link: string
}

export type AnalysesLastRateContextItem = {
  doctor: null
  dt_created: string
  first200: string
  lpu: {
    cropped_logo: string
    img_50: { url: string }
    img_100: { url: string }
    img_200: { url: string }
    logo?: {
      priority_image_files?: Array<{
        file: {
          url: string
        }
        file_format: string
      }>
    }
    name: string
    town: { translit: string }
    translit: string
  }
  patient_profile: {
    phone: string
  }
  private_user: boolean
}

export type AnalysesPageContext = {
  flavour: 'mobile' | 'full'
  feature_flags: FeatureFlags
  service_class: {
    group: string
    translit: string
    name: string
    name_vin: string
  }
  service_category?: {
    name: string
    name_vin: string
    translit?: string
  } | null
  equip_open_type?: boolean
  metro?: {
    name: string
    translit: string
  } | null
  district?: {
    name: string
    translit: string
    name_pred?: string | null
  } | null
  total_objects: number
  lpu_cards_service_aggregated_data?: {
    price_min: number | null
    price_max: number | null
  } | null
  request: {
    path?: string
    GET?: Record<string, string>
    country: {
      iso: string
    }
    user?: {
      is_staff?: boolean
      is_superuser?: boolean
    }
  }
  currency?: {
    symbol?: string
    iso?: string
    name_short_plural_genitive?: string
  }
  site_settings?: {
    current_award_year?: number
  }
  town: {
    id?: number
    translit: string
    name: string
    name_pred: string
    name_rod: string
    has_metro?: boolean
  }
  analyses_categories?: AnalysesCategoryContextItem[]
  popular_analyses?: PopularAnalysisContextItem[]
  popular_types?: PopularTypeContextItem[]
  last_rates?: AnalysesLastRateContextItem[]
  partner_cards?: PartnerCardContextItem[]
  lpu_cards_placement?: LpuCardsPageItem[]
  lpu_cards_page?: LpuCardsPage
  all_cards_page?: LpuCardsPage
}
